import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import clsx from "clsx";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import Table from "../../../../Components/v2/Table";
import FormError from "../../../../Components/v2/Form/FormError";
import DateTimeCell from "../../../../Components/v2/Table/DateTimeCell";
import AccountingWrapper from "../../../../Components/v2/Accounting/AccountingWrapper";
import AccountingPageHeader from "../../../../Components/v2/Accounting/AccountingPageHeader";
import Stats from "../../../../Components/v2/Stats";
import {
  addLocalNotification,
  clearAccountingTransaction,
  clearAcctTransactionError,
  listDeposits,
  requestDeleteAcctTransaction,
  requestGetIncomeStats,
  requestListAccountingTransaction,
  requestUpdateAcctTransaction,
  useAccountingDispatch,
  useAccountingState,
  useAuthState,
  useNotificationDispatch,
  usePeriodLock,
} from "../../../../Context";
import PeriodLockBanner from "../../../../Components/v2/Accounting/PeriodLockBanner";
import PeriodLockCell from "../../../../Components/v2/Accounting/PeriodLockCell";
import Pagination from "../../../../Components/v2/Pagination";
import {
  cancelAPIRequest,
  convertToTitleCase,
  formatCurrency,
  getErrorMessages,
  getHasPaymentsMessage,
  getPaymentCount,
} from "../../../../Utils";
import TableActions from "../../../../Components/v2/Table/TableActions";
import { ModalAlert } from "../../../../Components/ModalAlert";
import Modal from "../../../../Components/v2/Modal";
import Button from "../../../../Components/v2/Button";
import TransactionModal from "../../../../Components/v2/Accounting/TransactionModal";
import { PencilIcon, PlusIcon } from "@heroicons/react/outline";
import InvoiceModal from "../../../../Components/v2/Accounting/InvoiceModal";
import InvoiceDetailModal from "../../../../Components/v2/Accounting/InvoiceDetailModal";
import TransactionDetailModal from "../../../../Components/v2/Accounting/TransactionDetailModal";
import ExpandableDescription from "../../../../Components/v2/Accounting/ExpandableDescription";
import FormInput from "../../../../Components/v2/Form/FormInput";
import { useInvoiceLink } from "../../../../Hooks/useInvoiceLink";
import DepositsTab from "./DepositsTab";
import { isDepositOpen } from "../../../../Utils/DepositUtil";
import { wibStatsMonths, wibThisMonth } from "../../../../Utils/PeriodUtils";
import ErrorDialog from "../../../../Components/v2/ErrorDialog";

const AccountingIncomes = () => {
  const { t, i18n } = useTranslation();

  const [isIncomeModalOpen, setIsIncomeModalOpen] = useState(false);
  const [isTransactionDetailModalOpen, setIsTransactionDetailModalOpen] =
    useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [isInvoiceDetailModalOpen, setIsInvoiceDetailModalOpen] =
    useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [selectedIncome, setSelectedIncome] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isEditAmountModalOpen, setIsEditAmountModalOpen] = useState(false);
  const [editingValue, setEditingValue] = useState("");
  const [editingError, setEditingError] = useState(null);

  const accountingDispatch = useAccountingDispatch();
  const notificationDispatch = useNotificationDispatch();

  const {
    request,
    transactions,
    loading: incomeLoading,
    error: incomeError,
    nextTokens,
    deletedID,
    submitError,
    submitLoading,
    incomeStats,
    incomeStatsLoading,
    incomeStatsError,
    transactionID,
  } = useAccountingState();
  const { user } = useAuthState();
  const { isDateClosed } = usePeriodLock(user?.mainBusiness?.id);

  // ?tab=deposits opens Uang Muka; ?partnerId= narrows it to one customer
  // (Detail Kontak) and ?depositId= opens one deposit (Rekap PPN).
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab =
    searchParams.get("tab") === "deposits" ? "deposits" : "transactions";
  const partnerIDParam = searchParams.get("partnerId");
  const depositIDParam = searchParams.get("depositId");

  const setParams = (changes) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([key, value]) => {
      if (value) next.set(key, value);
      else next.delete(key);
    });
    setSearchParams(next, { replace: true });
  };

  // The whole list is loaded only while the Uang Muka tab is open. The tab
  // label's count (deposits that still have something left) needs just the
  // open ones, so that is all the Transaksi tab asks for.
  const [depositData, setDepositData] = useState({
    items: [],
    summary: { remaining: 0, count: 0 },
  });
  const [depositLoading, setDepositLoading] = useState(false);
  const [depositError, setDepositError] = useState(null);
  const [openDepositCount, setOpenDepositCount] = useState(0);
  const depositsTabOpen = activeTab === "deposits";

  const loadDeposits = useCallback(async () => {
    if (!user?.mainBusiness?.id) return;
    setDepositLoading(true);
    setDepositError(null);
    try {
      const data = await listDeposits({ businessID: user.mainBusiness.id });
      setDepositData(data);
      setOpenDepositCount(data.summary?.count || 0);
    } catch (error) {
      setDepositError(error);
    } finally {
      setDepositLoading(false);
    }
  }, [user?.mainBusiness?.id]);

  const loadOpenDepositCount = useCallback(async () => {
    if (!user?.mainBusiness?.id) return;
    try {
      const { items } = await listDeposits({
        businessID: user.mainBusiness.id,
        status: "open",
      });
      setOpenDepositCount(items.filter(isDepositOpen).length);
    } catch (error) {
      // Only the count on the tab label is missing; the tab still opens.
    }
  }, [user?.mainBusiness?.id]);

  useEffect(() => {
    if (depositsTabOpen) loadDeposits();
    else loadOpenDepositCount();
  }, [depositsTabOpen, loadDeposits, loadOpenDepositCount]);

  const partnerFilter = useMemo(() => {
    if (!partnerIDParam) return null;
    const match = depositData.items.find(
      (deposit) => deposit.partner?.id === partnerIDParam,
    );
    return { id: partnerIDParam, name: match?.partner?.name };
  }, [partnerIDParam, depositData.items]);

  // Reports link here to open one invoice, or the form for a new one.
  useInvoiceLink({
    onOpen: (invoice) => {
      setSelectedInvoice(invoice);
      setIsInvoiceDetailModalOpen(true);
    },
    onCreate: () => {
      setSelectedIncome(null);
      setIsInvoiceModalOpen(true);
    },
  });

  const filter = useMemo(
    () => ({
      or: [{ type: { eq: "INCOME" } }, { type: { eq: "INCOME_RECEIVABLE" } }],
    }),
    [],
  );

  const statsData = useMemo(() => {
    const { current, previous, top } = incomeStats || {};
    const totalThisMonth = current?.total || 0;
    const highestTransaction = top?.transaction || {};
    const highestAccount = top?.account || {};

    const calculatePercentageChange = (current, previous) => {
      const currentValue = typeof current === "number" ? current : 0;
      const previousValue = typeof previous === "number" ? previous : 0;

      // Handle the case where previousValue is 0
      if (previousValue === 0) {
        return {
          change: currentValue,
          direction: currentValue > 0 ? "up" : "no-change",
          color: currentValue > 0 ? "green" : "gray",
        };
      }

      const change = ((currentValue - previousValue) / previousValue) * 100;

      // Check for no change
      if (change === 0) {
        return {
          change: 0,
          direction: "no-change",
          color: "gray",
        };
      }

      return {
        change: change.toFixed(2) + "%",
        direction: change >= 0 ? "up" : "down",
        color: change >= 0 ? "green" : "red",
      };
    };

    const percentageChangeData = calculatePercentageChange(
      current?.total,
      previous?.total,
    );

    return [
      {
        title: t("incomes.stats.labels.totalThisMonth"),
        value: totalThisMonth,
        footer:
          percentageChangeData.direction === "no-change"
            ? t("incomes.stats.labels.noChange")
            : `${
                typeof percentageChangeData.change === "number"
                  ? new Intl.NumberFormat(i18n.language, {
                      notation: "compact",
                      compactDisplay: "short",
                    }).format(percentageChangeData.change)
                  : percentageChangeData.change
              } ${
                percentageChangeData.direction === "up"
                  ? t("incomes.stats.labels.higher")
                  : t("incomes.stats.labels.lower")
              }`,
        trend: percentageChangeData,
      },
      {
        title: t("incomes.stats.labels.highestTransaction"),
        value: highestTransaction.total || 0,
        footer: highestTransaction.invoiceNumber ? (
          <a
            href={`/invoice/${highestTransaction.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline"
          >
            Invoice #{highestTransaction.invoiceNumber}
          </a>
        ) : highestTransaction.id ? (
          <a
            href={`/accounting-transaction/create?id=${highestTransaction.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline"
          >
            {t("incomes.stats.labels.transaction")}{" "}
            {highestTransaction.id?.slice(-5).toUpperCase()}
          </a>
        ) : (
          t("common.noData")
        ),
      },
      {
        title: t("incomes.stats.labels.highestIncome"),
        value: highestAccount.total || 0,
        footer: highestAccount.name || t("common.noData"),
      },
    ];
  }, [incomeStats, t, i18n.language]);

  // Clear errors on unmount
  useEffect(() => {
    return () => {
      if (request) {
        cancelAPIRequest(request);
      }
      clearAccountingTransaction(accountingDispatch);
      clearAcctTransactionError(accountingDispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user.mainBusiness?.id) {
      requestListAccountingTransaction(accountingDispatch, {
        page: 1,
        limit: perPage,
        businessID: user.mainBusiness.id,
        createdAt: {
          between: [
            wibThisMonth().startDate,
            wibThisMonth().endDate,
          ],
        },
        filter,
        // Payment rows are dropped before the page is
        // counted, so a page can no longer come back empty.
        excludeChildren: true,
      });
    }
  }, [accountingDispatch, user.mainBusiness?.id, perPage, filter]);

  //  Handle delete success
  useEffect(() => {
    if (deletedID) {
      // Close modal
      setShowConfirmModal(false);
      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `delete-income-${deletedID}`,
        type: "success",
        title: "common.success",
        body: "incomes.notifications.deleteSuccess",
        showFull: true,
      });
    }
  }, [notificationDispatch, deletedID]);

  // Handle inline edit success
  useEffect(() => {
    if (transactionID && isEditAmountModalOpen) {
      addLocalNotification(notificationDispatch, {
        id: `edit-transaction-${transactionID}`,
        type: "success",
        title: "common.success",
        body: "transactions.notifications.updateSuccess",
        showFull: true,
      });
      setSelectedIncome(null);
      setEditingValue("");
      setEditingError(null);
      setIsEditAmountModalOpen(false);
      clearAccountingTransaction(accountingDispatch);
    }
  }, [
    transactionID,
    isEditAmountModalOpen,
    notificationDispatch,
    accountingDispatch,
  ]);

  useEffect(() => {
    if (submitError) {
      setShowConfirmModal(false);
      // A failed amount edit stays open with what was typed and says why, so
      // it can be corrected and sent again.
      if (isEditAmountModalOpen) {
        setEditingError(getErrorMessages(submitError, t).join(" "));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitError]);

  useEffect(() => {
    if (
      submitError &&
      !showConfirmModal &&
      !isEditAmountModalOpen &&
      !isIncomeModalOpen &&
      !isInvoiceModalOpen &&
      !isInvoiceDetailModalOpen
    ) {
      setShowErrorModal(true);
    }
  }, [
    submitError,
    showConfirmModal,
    isEditAmountModalOpen,
    isIncomeModalOpen,
    isInvoiceModalOpen,
    isInvoiceDetailModalOpen,
  ]);

  useEffect(() => {
    if (user?.mainBusiness?.id) {
      onLoadStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountingDispatch, user?.mainBusiness?.id]);

  const onLoadStats = () => {
    requestGetIncomeStats(accountingDispatch, {
      businessID: user.mainBusiness.id,
      ...wibStatsMonths(),
    });
  };

  const onPageChange = (selectedPage) => {
    setPage(selectedPage);
    if (nextTokens[selectedPage] || selectedPage === 1) {
      requestListAccountingTransaction(accountingDispatch, {
        page: selectedPage,
        limit: perPage,
        businessID: user.mainBusiness.id,
        createdAt: {
          between: [
            wibThisMonth().startDate,
            wibThisMonth().endDate,
          ],
        },
        nextToken: nextTokens[selectedPage],
        filter,
        // Payment rows are dropped before the page is
        // counted, so a page can no longer come back empty.
        excludeChildren: true,
      });
    }
  };

  const onPerPageChange = (itemsPerPage) => {
    setPerPage(itemsPerPage);
  };

  // Every write entry point re-checks rather than trusting its caller: a row can
  // be locked from another tab between render and click.
  const handleDelete = (income) => {
    if (isDateClosed(income?.createdAt)) return;
    setSelectedIncome(income);
    setShowConfirmModal(true);
  };

  const handleClose = (shouldRefresh) => {
    // An invoice form or drawer may have used deposits.
    const touchedDeposits = isInvoiceModalOpen || isInvoiceDetailModalOpen;
    cancelAPIRequest(request);
    setIsIncomeModalOpen(false);
    setIsTransactionDetailModalOpen(false);
    setSelectedIncome(null);
    setIsInvoiceModalOpen(false);
    setIsInvoiceDetailModalOpen(false);
    setSelectedInvoice(null);
    if (touchedDeposits) {
      if (depositsTabOpen) loadDeposits();
      else loadOpenDepositCount();
    }
    if (shouldRefresh && user.mainBusiness?.id) {
      requestListAccountingTransaction(accountingDispatch, {
        page: 1,
        limit: perPage,
        businessID: user.mainBusiness.id,
        createdAt: {
          between: [
            wibThisMonth().startDate,
            wibThisMonth().endDate,
          ],
        },
        filter,
        // Payment rows are dropped before the page is
        // counted, so a page can no longer come back empty.
        excludeChildren: true,
      });
    }
  };

  const handleEdit = (income) => {
    if (isDateClosed(income?.createdAt)) return;
    setSelectedIncome(income);
    if (income?.invoiceNumber) {
      setIsInvoiceModalOpen(true);
    } else {
      setIsIncomeModalOpen(true);
    }
  };

  const handleStartEdit = (transaction) => {
    if (isDateClosed(transaction?.createdAt)) return;
    setSelectedIncome(transaction);
    setEditingValue(transaction.total.toString());
    setEditingError(null);
    setIsEditAmountModalOpen(true);
  };

  const handleCancelEdit = () => {
    cancelAPIRequest(request);
    setSelectedIncome(null);
    setEditingValue("");
    setEditingError(null);
    setIsEditAmountModalOpen(false);
    // The failure was shown in this modal; don't raise it again on close.
    clearAcctTransactionError(accountingDispatch);
  };

  const handleSaveEdit = () => {
    if (isDateClosed(selectedIncome?.createdAt)) return;
    if (!editingValue || editingValue.trim() === "") {
      setEditingError("transactions.errors.amountRequired");
      return;
    }

    const newTotal = Number(editingValue);
    if (isNaN(newTotal) || newTotal <= 0) {
      setEditingError("accounting.transactions.modal.error.invalidAmount");
      return;
    }

    setEditingError(null);

    const input = {
      transaction_id: selectedIncome.id,
      business_id: user.mainBusiness.id,
      accounts:
        selectedIncome.accounts?.items?.map((account) => ({
          id: account.accountID,
          debit: account.debit > 0 ? newTotal : 0,
          credit: account.credit > 0 ? newTotal : 0,
        })) || [],
      type: selectedIncome.type,
      paid: selectedIncome.paid,
      created_at: new Date(selectedIncome.createdAt),
      description: selectedIncome.description,
      total: newTotal,
      payment_due_at: new Date(selectedIncome.paymentDueAt),
      partner_id: selectedIncome.partnerID || null,
    };

    requestUpdateAcctTransaction(accountingDispatch, { input });
  };

  // The server refuses to delete an invoice that still has payments, so the
  // dialog says so up front and its button stays disabled.
  const selectedPaymentCount = getPaymentCount(selectedIncome);

  const onDelete = () => {
    if (selectedPaymentCount > 0) return;
    requestDeleteAcctTransaction(accountingDispatch, {
      id: selectedIncome?.id,
    });
  };

  const getDeleteDescription = () => {
    if (!selectedIncome) return null;
    const date = dayjs(selectedIncome.createdAt)
      .locale(i18n.language)
      .format("D MMM YYYY");
    const amount = formatCurrency(selectedIncome.total ?? 0);
    const summary = selectedIncome.description
      ? t("incomes.modal.description.deleteItem", {
          description: selectedIncome.description,
          amount,
          date,
        })
      : t("incomes.modal.description.deleteItemNoDesc", { amount, date });
    if (selectedPaymentCount === 0) return summary;
    // Spans, not divs: ModalAlert renders this inside a <p>.
    return (
      <>
        {summary}
        <span className="mt-2 block font-medium text-red-600">
          {t("accounting.transactions.modal.description.hasPayments", {
            count: selectedPaymentCount,
          })}
        </span>
      </>
    );
  };

  const SkeletonStats = () => {
    return (
      <dl className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={index}
            className="px-4 py-5 shadow rounded-lg overflow-hidden sm:p-6 animate-pulse"
          >
            <dt className="text-sm font-medium text-gray-500 truncate">
              <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">
              <div className="h-8 bg-gray-300 rounded w-1/2"></div>
            </dd>
            <dd className="mt-2 flex items-center text-sm text-gray-600">
              <div className="h-4 bg-gray-300 rounded w-1/3"></div>
            </dd>
          </div>
        ))}
      </dl>
    );
  };


  return (
    <AccountingWrapper>
      <main className="lg:col-span-9">
        {/* Header */}
        <AccountingPageHeader
          title="incomes.title"
          additionalActions={
            <button
              type="button"
              onClick={() => setIsInvoiceModalOpen(true)}
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-full sm:w-auto"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              {t("incomes.action.invoice")}
            </button>
          }
          primaryAction={{
            label: "incomes.header.title.add",
            onClick: () => setIsIncomeModalOpen(true),
            icon: <PlusIcon className="h-5 w-5" />,
          }}
        />

        {/* Stats */}
        <div className="mt-6 px-4 sm:px-6 lg:px-8">
          {incomeStatsLoading ? (
            <SkeletonStats />
          ) : incomeStatsError ? (
            <FormError
                error={incomeStatsError}
                title="common.errors.dataTitle"
                onRetry={onLoadStats}
              />
          ) : (
            <Stats stats={statsData} />
          )}
        </div>

        {/* Tabs */}
        <div className="mt-6 px-4 sm:px-6 lg:px-8">
          <div
            role="tablist"
            aria-label={t("incomes.title")}
            className="flex gap-6 overflow-x-auto border-b border-gray-200"
          >
            {[
              { id: "transactions", label: t("deposits.tabs.transactions") },
              {
                id: "deposits",
                label: t("deposits.tabs.deposits"),
                count: openDepositCount,
              },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                onClick={() =>
                  setParams({
                    tab: tab.id === "deposits" ? "deposits" : null,
                    partnerId: null,
                    depositId: null,
                  })
                }
                className={clsx(
                  "-mb-px inline-flex items-center gap-2 whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium",
                  activeTab === tab.id
                    ? "border-blue-600 text-blue-700"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",
                )}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span
                    className={clsx(
                      "rounded-full px-2 py-0.5 text-xs tabular-nums",
                      activeTab === tab.id
                        ? "bg-blue-100 text-blue-700"
                        : "bg-gray-100 text-gray-600",
                    )}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {activeTab === "deposits" ? (
          <DepositsTab
            businessID={user?.mainBusiness?.id}
            deposits={depositData.items}
            summary={depositData.summary}
            loading={depositLoading}
            error={depositError}
            onReload={loadDeposits}
            partnerFilter={partnerFilter}
            onClearPartnerFilter={() => setParams({ partnerId: null })}
            openDepositID={depositIDParam}
            onDepositOpened={() => setParams({ depositId: null })}
          />
        ) : (
        <>
        {/* Income List */}
        <div className="px-4 py-5 sm:px-6 lg:px-8">
          <PeriodLockBanner
            businessID={user?.mainBusiness?.id}
            className="mb-4"
          />
          <Table
            isLoading={incomeLoading}
            error={incomeError}
            onRetry={() => onPageChange(page)}
            columns={[
              {
                header: "incomes.fields.date",
                accessor: (item) => <DateTimeCell value={item.createdAt} />,
                sortable: true,
                // The rows here are raw transactions, so the sortable field is
                // `createdAt`; "date" matched nothing and sorted nothing.
                sortKey: "createdAt",
              },
              {
                header: "incomes.fields.description",
                accessor: (item) => {
                  let descriptionText = "";
                  if (item.description) {
                    descriptionText = item.description;
                  } else if (item.invoiceNumber) {
                    descriptionText = `Invoice #${item.invoiceNumber}`;
                  } else {
                    descriptionText = t("incomes.noDescription");
                  }

                  return (
                    <div className="flex items-center max-w-xs whitespace-normal break-words">
                      {!item.description && !item.invoiceNumber ? (
                        <span className="text-gray-400 italic">
                          {descriptionText}
                        </span>
                      ) : (
                        <ExpandableDescription text={descriptionText} />
                      )}
                    </div>
                  );
                },
              },
              {
                header: "incomes.fields.type",
                accessor: (item) => t(convertToTitleCase(item.type)),
              },
              {
                header: "incomes.fields.amount",
                sortable: true,
                sortKey: "price",
                accessor: (item, index) => {
                  const originalTransaction = transactions[index];
                  // Clicking the amount opens an edit modal, so a locked row
                  // must lose the click target, hover, cursor, role and pencil
                  // together — the number itself stays readable.
                  const editable =
                    !originalTransaction?.invoiceNumber &&
                    !isDateClosed(item.createdAt);

                  return (
                    <div
                      className={`flex items-center justify-end px-2 py-1 rounded-md transition-colors duration-200 -mr-2 ${
                        editable ? "cursor-pointer group hover:bg-gray-100" : ""
                      }`}
                      onClick={() => {
                        if (editable) {
                          handleStartEdit(originalTransaction);
                        }
                      }}
                      role={editable ? "button" : undefined}
                      title={
                        editable
                          ? t("accounting.dashboard.transaction.clickToEdit")
                          : ""
                      }
                    >
                      <span className="font-medium">
                        Rp{item.total.toLocaleString()}
                      </span>
                      <div className="w-3.5 h-3.5 ml-2 flex-shrink-0">
                        {editable && (
                          <PencilIcon
                            className="w-full h-full text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                            aria-hidden="true"
                          />
                        )}
                      </div>
                    </div>
                  );
                },
                align: "right",
              },
              {
                header: "common.actions.label",
                accessor: (income, index) => {
                  const viewIncome = () => {
                    if (income?.invoiceNumber) {
                      setSelectedInvoice(transactions[index]);
                      setIsInvoiceDetailModalOpen(true);
                    } else {
                      setSelectedIncome(income);
                      setIsTransactionDetailModalOpen(true);
                    }
                  };

                  // Cell swapped whole: TableActions changes shape between one
                  // action and several, so pruning the list would rewrite the
                  // control instead of just restricting it.
                  if (isDateClosed(income.createdAt)) {
                    return (
                      <PeriodLockCell
                        className="justify-end"
                        onView={viewIncome}
                      />
                    );
                  }

                  return (
                    <TableActions
                      position={
                        index === transactions.length - 1 ? "top" : "bottom"
                      }
                      actions={[
                        {
                          label: t("common.view"),
                          onClick: viewIncome,
                        },
                        {
                          label: t("common.edit"),
                          onClick: () => handleEdit(income),
                        },
                        {
                          label: t("common.delete"),
                          onClick: () => handleDelete(income),
                          variant: "danger",
                        },
                      ]}
                    />
                  );
                },
                align: "right",
              },
            ]}
            data={transactions}
          />
        </div>

        {/* Pagination */}
        <Pagination
          nextTokens={nextTokens}
          currentPage={page}
          isLoading={incomeLoading}
          perPage={perPage}
          onPageChange={onPageChange}
          onPerPageChange={onPerPageChange}
        />
        </>
        )}

        {/* Transaction Modal */}
        {isIncomeModalOpen && (
          <TransactionModal
            open={isIncomeModalOpen}
            onClose={handleClose}
            transaction={selectedIncome}
            transactionType="INCOME"
          />
        )}

        {isTransactionDetailModalOpen && (
          <TransactionDetailModal
            open={isTransactionDetailModalOpen}
            onClose={() => {
              setIsTransactionDetailModalOpen(false);
              setSelectedIncome(null);
            }}
            onEdit={(income) => {
              setIsTransactionDetailModalOpen(false);
              handleEdit(income);
            }}
            transaction={selectedIncome}
          />
        )}

        {/* Invoice Modal */}
        {isInvoiceModalOpen && (
          <InvoiceModal
            open={isInvoiceModalOpen}
            onClose={handleClose}
            invoice={selectedIncome}
          />
        )}

        {/* Invoice Detail Modal */}
        {isInvoiceDetailModalOpen && (
          <InvoiceDetailModal
            open={isInvoiceDetailModalOpen}
            invoice={selectedInvoice}
            onClose={() => {
              setIsInvoiceDetailModalOpen(false);
              setSelectedInvoice(null);
              // Pakai Uang Muka may have changed what is left.
              if (depositsTabOpen) loadDeposits();
              else loadOpenDepositCount();
            }}
            onEdit={(invoice) => {
              setIsInvoiceDetailModalOpen(false);
              setSelectedIncome(invoice);
              setIsInvoiceModalOpen(true);
            }}
          />
        )}

        {/* Delete Modal */}
        <ModalAlert
          open={showConfirmModal}
          setOpen={(open) => {
            setShowConfirmModal(open);
          }}
          title="incomes.modal.title.delete"
          description="incomes.modal.description.delete"
          descriptionNode={getDeleteDescription()}
          confirmButtonText="common.delete"
          cancelButtonText="Cancel"
          loadingButton={submitLoading}
          disabledButton={submitLoading || selectedPaymentCount > 0}
          onConfirm={onDelete}
          onCancel={() => {
            cancelAPIRequest(request);
            setShowConfirmModal(false);
            setSelectedIncome(null);
          }}
        />

        {/* Error Modal */}
        {submitError && (
          <ErrorDialog
            open={showErrorModal}
            message={getHasPaymentsMessage(submitError)}
            error={submitError}
            onClose={() => {
              setShowErrorModal(false);
              clearAcctTransactionError(accountingDispatch);
            }}
          />
        )}

        {/* Edit Amount Modal */}
        <Modal
          open={isEditAmountModalOpen}
          onClose={handleCancelEdit}
          title="accounting.dashboard.transaction.clickToEdit"
          footer={
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                onClick={handleCancelEdit}
                variant="secondary"
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="button"
                onClick={handleSaveEdit}
                disabled={submitLoading}
                variant="primary"
              >
                {t("common.save")}
              </Button>
            </div>
          }
        >
          <FormInput
            label={t("transactions.fields.amount")}
            value={editingValue}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !submitLoading) {
                handleSaveEdit();
              }
            }}
            // The plain number is kept, as in TransactionModal: fed back as
            // "3.000", the formatted text had its "." read as a decimal point.
            numberFormat={{
              thousandSeparator: ".",
              decimalSeparator: ",",
              allowedDecimalSeparators: [","],
              valueIsNumericString: true,
              onValueChange: ({ value }) => {
                setEditingValue(value);
                if (editingError) setEditingError(null);
              },
            }}
            prefix="Rp"
            error={editingError}
            disabled={submitLoading}
            onClear={() => setEditingValue("")}
            autoFocus
          />
        </Modal>
      </main>
    </AccountingWrapper>
  );
};

export default AccountingIncomes;
